import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Calculator, ShieldOff } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PageContainer } from '~/components/page-container';
import { ReviewsGrid } from '~/components/catalog/reviews/reviews-grid';
import { RecommendationsGrid } from '~/components/catalog/reviews/recommendations-grid';
import { ReviewExcludedGroupsDialog } from '~/components/catalog/reviews/review-excluded-groups-dialog';
import { useHasAccess } from '~/lib/auth/use-has-access';
import { serverMessage } from '~/lib/api/server-error';
import { useRecalculateRatings } from '~/hooks/use-reviews';

/** Product reviews moderation — customer comments, contributor reviews and recommendations (legacy pages, unified under tabs). */
export function Reviews() {
	const { t } = useTranslation();
	const [excludedOpen, setExcludedOpen] = useState(false);
	const [recalcOpen, setRecalcOpen] = useState(false);
	const hasAccess = useHasAccess();
	// Review exclusions are CustomerAccessRules CRUD, gated server-side by the System role — only show the entry point to System operators.
	const canManageExclusions = hasAccess('System');
	const canRecalculate = hasAccess('Catalog');
	const recalculate = useRecalculateRatings();

	const confirmRecalculate = () =>
		recalculate.mutate(undefined, {
			onSuccess: () => {
				toast.success(t('catalog.reviews.recalculate.done'));
				setRecalcOpen(false);
			},
			onError: err => toast.error(serverMessage(err))
		});

	return (
		<PageContainer>
			<div className="flex flex-col gap-4">
				<div className="flex items-center justify-between gap-4">
					<h1 className="text-2xl font-semibold tracking-[-0.015em] text-foreground">{t('catalog.reviews.title')}</h1>
					<div className="flex items-center gap-2">
						{canRecalculate && (
							<Button variant="outline" onClick={() => setRecalcOpen(true)}>
								<Calculator size={15} /> {t('catalog.reviews.recalculate.button')}
							</Button>
						)}
						{canManageExclusions && (
							<Button variant="outline" onClick={() => setExcludedOpen(true)}>
								<ShieldOff size={15} /> {t('catalog.reviews.excludedGroups.button')}
							</Button>
						)}
					</div>
				</div>
				<Tabs defaultValue="customer">
					<TabsList>
						<TabsTrigger value="customer">{t('catalog.reviews.tabs.customer')}</TabsTrigger>
						<TabsTrigger value="contributor">{t('catalog.reviews.tabs.contributor')}</TabsTrigger>
						<TabsTrigger value="recommendations">{t('catalog.reviews.tabs.recommendations')}</TabsTrigger>
					</TabsList>
					<TabsContent value="customer" className="pt-4">
						<ReviewsGrid kind="customer" />
					</TabsContent>
					<TabsContent value="contributor" className="pt-4">
						<ReviewsGrid kind="contributor" />
					</TabsContent>
					<TabsContent value="recommendations" className="pt-4">
						<RecommendationsGrid />
					</TabsContent>
				</Tabs>
			</div>
			<ReviewExcludedGroupsDialog open={excludedOpen} onOpenChange={setExcludedOpen} />
			<Dialog open={recalcOpen} onOpenChange={setRecalcOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('catalog.reviews.recalculate.title')}</DialogTitle>
						<DialogDescription>{t('catalog.reviews.recalculate.body')}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setRecalcOpen(false)}>
							{t('common.cancel')}
						</Button>
						<Button onClick={confirmRecalculate} disabled={recalculate.isPending}>
							{recalculate.isPending ? t('common.saving') : t('catalog.reviews.recalculate.confirm')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</PageContainer>
	);
}
